// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { Flex, MantineProvider } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Navigate, Route, Routes } from 'react-router';

import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { AcceptInvitation } from './AcceptInvitation';
import { ForgotPassword } from './ForgotPassword/ForgotPassword';
import { ssoAuthPath } from './Login/helper';
import { Login } from './Login/Login';
import { LoginMicrosoftSSO } from './Login/LoginMicrosoftSSO';
import { ResetPassword } from './ResetPassword';
import { VerifyEmailPrompt } from './VerifyEmailPrompt';

const AuthBackground = React.lazy(() => import('./AuthBackground'));

export const AuthenticationRoutes = () => {
  const isVertical = useMediaQuery('(max-width: 1023px)', false, {
    getInitialValueInEffect: false,
  });
  const { user } = useMaybeUser();

  if (user?.acceptedLatestEula) {
    return <Navigate to="/" />;
  }

  return (
    <MantineProvider theme={{ primaryColor: 'indigo' }}>
      <Flex
        direction={isVertical ? 'column' : 'row-reverse'}
        style={{
          backgroundColor: 'white',
        }}
      >
        <AuthBackground isMobile={isVertical} />
        <Flex
          direction="row"
          align="center"
          justify="center"
          p="md"
          style={
            isVertical
              ? {
                  width: '100%',
                  // dvh, not vh: on phones 100vh is the URL-bar-collapsed viewport, so a
                  // vh-derived height overflows the visible area and makes the page scrollable.
                  minHeight: 'calc(100dvh - 120px)',
                }
              : {
                  width: '40%',
                  minHeight: 'calc(100dvh - 425px)',
                  maxWidth: '700px',
                }
          }
        >
          <Routes>
            <Route path="login" element={<Login />} />
            <Route path={ssoAuthPath} element={<LoginMicrosoftSSO />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="password-reset" element={<ResetPassword />} />
            <Route path="email-confirmation" element={<VerifyEmailPrompt />} />
            <Route path="accept" element={<AcceptInvitation />} />
            <Route path="*" element={<Navigate to="login" />} />
          </Routes>
        </Flex>
      </Flex>
    </MantineProvider>
  );
};
